import React from "react";
import { Link } from "react-router-dom";

import { IMG } from "../lib/assets";
import { Reveal, AnimatedHeading, FadeImage } from "../components/Reveal";
import { PageShell, PageHeader } from "../components/PageShell";
import { MandalaImprint, PaisleyDivider } from "../components/Motifs";

const VALUES = [
  {
    title: "Heritage",
    text: "Every ritual honours Indian craft — from ubtan to zari-fine detailing.",
  },
  {
    title: "Artistry",
    text: "Our artists train relentlessly, treating each client as a living canvas.",
  },
  {
    title: "Sanctuary",
    text: "A calm, golden space designed to slow time and restore the spirit.",
  },
  {
    title: "Purity",
    text: "Clean, conscious products that respect your skin and the earth.",
  },
];

const STATS = [
  ["12+", "Years of craft"],
  ["8", "Master artists"],
  ["5k+", "Radiant clients"],
  ["100%", "Made by hand"],
];

const TEAM = [
  { name: "Meera Rao", role: "Bridal Director", img: IMG.brideFloral },
  { name: "Kabir Sen", role: "Master Stylist", img: IMG.hairBlow },
  { name: "Anaya Iyer", role: "Skin Therapist", img: IMG.spaMask },
];

export default function About() {
  return (
    <PageShell>
      <PageHeader
        overline="The Atelier"
        title="Radiance, woven by hand."
        subtitle="Zaya & Zari was born from a belief that beauty is a craft — patient, cultural and deeply personal."
        image={IMG.interiorChairs}
      />

      {/* Philosophy split */}
      <section className="relative overflow-hidden py-28 sm:py-36">
        <MandalaImprint className="absolute -right-40 top-10" size={560} opacity={0.05} />
        <div className="mx-auto grid max-w-wide grid-cols-1 items-center gap-16 px-6 sm:px-12 md:grid-cols-[1.618fr_1fr] lg:gap-28">
          <div className="relative md:order-1">
            <FadeImage src={IMG.brideRedGold} alt="Bridal artistry" className="aspect-[4/5] w-full" />
            <div className="absolute -bottom-6 -left-6 h-40 w-40 rounded-3xl border border-gold/40 bg-ink-800" />
          </div>
          <div className="md:order-2">
            <Reveal>
              <p className="overline text-gold">Our Philosophy</p>
            </Reveal>
            <AnimatedHeading
              text="Two words, one promise."
              className="mt-6 font-display text-4xl font-light text-cream-50 sm:text-5xl"
            />
            <Reveal delay={0.2}>
              <p className="mt-8 text-cream-50/70 leading-relaxed">
                <span className="text-gold">Zaya</span> — radiance. The glow that
                rises when one feels truly seen. <span className="text-gold">Zari</span> —
                the golden thread India weaves into its most treasured textiles.
              </p>
              <p className="mt-4 text-cream-50/70 leading-relaxed">
                We marry the two: an inner radiance, finished with handcrafted,
                heirloom-grade detail. No shortcuts. No noise. Only beauty that
                feels like a homecoming.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="bg-ink-800/40 py-24">
        <div className="mx-auto max-w-wide px-6 sm:px-12">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.1}>
                <div className="h-full rounded-2xl border border-gold/15 bg-ink-900 p-8">
                  <span className="font-display text-3xl text-gold">0{i + 1}</span>
                  <h3 className="mt-4 font-display text-2xl text-cream-50">{v.title}</h3>
                  <p className="mt-3 text-sm text-cream-50/60 leading-relaxed">{v.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-24">
        <div className="mx-auto grid max-w-wide grid-cols-2 gap-8 px-6 sm:px-12 lg:grid-cols-4">
          {STATS.map(([num, label], i) => (
            <Reveal key={label} delay={i * 0.1}>
              <div className="text-center">
                <p className="font-display text-6xl font-light text-gold sm:text-7xl">{num}</p>
                <p className="mt-2 text-xs uppercase tracking-[0.2em] text-cream-50/60">{label}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <PaisleyDivider className="mb-8" />

      {/* Team */}
      <section className="py-20">
        <div className="mx-auto max-w-wide px-6 sm:px-12">
          <div className="text-center">
            <Reveal>
              <p className="overline text-gold">The Artists</p>
            </Reveal>
            <AnimatedHeading
              text="Hands behind the glow"
              className="mt-5 font-display text-4xl font-light text-cream-50 sm:text-6xl"
            />
          </div>
          <div className="mt-16 grid grid-cols-1 gap-8 sm:grid-cols-3">
            {TEAM.map((m, i) => (
              <Reveal key={m.name} delay={i * 0.12}>
                <div className="group">
                  <FadeImage
                    src={m.img}
                    alt={m.name}
                    className="aspect-[3/4] w-full"
                    imgClassName="transition-transform duration-700 group-hover:scale-105"
                  />
                  <h3 className="mt-5 font-display text-2xl text-cream-50">{m.name}</h3>
                  <p className="text-xs uppercase tracking-[0.2em] text-gold">{m.role}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 text-center">
        <AnimatedHeading
          text="Come, let us craft your radiance."
          className="mx-auto max-w-3xl font-display text-4xl font-light text-cream-50 sm:text-6xl"
        />
        <Reveal delay={0.2}>
          <Link
            to="/contact"
            className="mt-8 inline-block rounded-full bg-gold px-10 py-5 text-xs uppercase tracking-[0.25em] text-ink-900 transition-colors hover:bg-antique"
          >
            Book a Visit
          </Link>
        </Reveal>
      </section>
    </PageShell>
  );
}
